<template>
  <div class="m-chat-page">
    <header class="m-chat-header">
      <button class="back-button" @click="goBack">
        ‹
      </button>

      <img :src="interlocutor.avatar" class="avatar" alt="Avatar" />

      <div class="info">
        <h3 class="name">{{ interlocutor.name }}</h3>
        <div class="status-row">
          <p class="specifics" :class="{ typing: isInterlocutorTyping }">
            {{ isInterlocutorTyping ? 'печатает...' : interlocutor.specifics }}
          </p>
        </div>
      </div>
    </header>

    <div ref="messagesList" class="messages-list">
      <template v-if="isLoadingMessages && chatId">
        <div class="skeleton-container">
          <div
            v-for="n in 6"
            :key="n"
            :class="['message-skeleton-wrapper', n % 2 === 0 ? 'own' : 'interlocutor']"
          >
            <div v-if="n % 2 !== 0" class="skeleton-avatar shimmer"></div>

            <div
              class="message-skeleton-bubble shimmer"
              :style="{ width: `${62 + n * 3}%` }"
            >
              <div class="skeleton-line short shimmer"></div>
              <div class="skeleton-line shimmer"></div>
            </div>
          </div>
        </div>
      </template>

      <template v-else-if="isGhostChat && messages.length === 0">
        <div class="empty-chat-placeholder">
          <img
            src="../assets/empty-ghost-chat.png"
            alt="Нет сообщений"
            class="empty-chat-icon"
          />
          <p class="empty-chat-text">Пока что здесь нет сообщений</p>
          <p class="empty-chat-subtext">Напишите первым, чтобы начать общение</p>
        </div>
      </template>

      <template v-else>
        <div
          v-for="message in messages"
          :key="message.id"
          :ref="el => observeMessage(el, message)"
          :class="[
            'message-item',
            {
              'own-message': message.senderId === currentUserId,
              'sending-message': message.isSending,
              'failed-message': message.isFailed
            }
          ]"
          @touchstart="startPressTimer(message)"
          @touchend="cancelPressTimer"
          @mousedown="startPressTimer(message)"
          @mouseup="cancelPressTimer"
          @mouseleave="cancelPressTimer"
        >
          <div v-if="message.senderId !== currentUserId" class="avatar-container">
            <img :src="interlocutor.avatar" class="message-avatar" alt="Avatar" />
          </div>

          <div class="message-content">
            <p class="text" :class="{ deleted: message.is_deleted }">
              <i v-if="message.is_deleted">сообщение удалено</i>
              <template v-else>
                <div v-if="message.imageUrl" class="message-image-wrapper">
                  <div v-if="!loadedImages[message.id]" class="image-shimmer"></div>

                  <img
                    :key="message.imageUrl"
                    :src="message.imageUrl"
                    class="message-image"
                    :class="{ loaded: loadedImages[message.id] }"
                    alt="image"
                    @load="loadedImages[message.id] = true"
                    @click.stop="openImage(message.imageUrl)"
                  />
                </div>

                <div
                  v-else-if="message.attachmentUrl"
                  class="file-message"
                  @click.stop="downloadFile(message)"
                >
                  <div class="file-icon">{{ fileIcon(message) }}</div>

                  <div class="file-info">
                    <div class="file-name">
                      {{ message.attachmentName || 'Файл' }}
                    </div>
                    <div class="file-meta">
                      {{ downloadingFiles[message.id] ? 'Скачивание…' : fileMeta(message) }}
                    </div>
                  </div>

                  <div class="file-download">
                    <span v-if="downloadingFiles[message.id]" class="file-spinner"></span>
                    <span v-else>↓</span>
                  </div>
                </div>

                <p v-else class="text">
                  {{ message.textMessage }}
                </p>
              </template>
            </p>

            <span class="time">
              {{ formatTime(message.sendDate) }}

              <template v-if="!message.isSending && !message.isFailed">
                <template v-if="message.isRead">
                  <img
                    :src="message.senderId === currentUserId ? '/is-read.png' : '/is-read-interlocator.png'"
                    class="read-icon"
                    alt=""
                  />
                </template>

                <template v-else>
                  <img
                    :src="message.senderId === currentUserId ? '/is-unread.png' : '/is-unread-interlocator.png'"
                    class="read-icon"
                    alt=""
                  />
                </template>
              </template>
            </span>

            <div
              v-if="message.isSending"
              class="mobile-sending-spinner"
            ></div>

            <button
              v-else-if="message.isFailed"
              class="mobile-retry-button"
              @click.stop="retryMessage(message)"
            >
              Повторить
            </button>
          </div>
        </div>
      </template>
    </div>

    <div class="message-input">
      <button
        class="file-button"
        @click="openFilePicker"
      >
        <img src="/add-file.png" alt="Attach" />
      </button>

      <input
        v-model="newMessage"
        type="text"
        placeholder="Напишите сообщение"
        class="message-field"
        @input="handleTyping"
        @keyup.enter="sendMessage"
      />

      <button
        class="send-button"
        @click="sendMessage"
        :disabled="!newMessage.trim()"
      >
        <img src="/send-message.png" alt="Send" />
      </button>
    </div>

    <input
      ref="fileInput"
      type="file"
      style="display:none"
      @change="handleFileSelected"
    />

    <transition name="fade">
      <div
        v-if="showDeleteMenu"
        class="delete-popup-overlay"
        @click.self="showDeleteMenu = false"
      >
        <div class="delete-popup">
          <p>Удалить сообщение?</p>
          <button class="delete-button" @click="deleteMessage">Удалить</button>
          <button class="cancel-button" @click="showDeleteMenu = false">Отмена</button>
        </div>
      </div>
    </transition>
  </div>
</template>
  
  <script setup>
  import { ref, nextTick, computed, onMounted, onUnmounted } from "vue";
  import { useRouter, useRoute } from "vue-router";
  
  import ChatsService from "../api/ChatsService";

  import { authStateReady } from "../utils/authState";
  import {
  getFirestore,
  collection,
  query,
  where,
  getDocs,
  writeBatch,
  serverTimestamp,
  onSnapshot
} from "firebase/firestore";

  
  const router = useRouter();
  const route = useRoute();
  
  const currentUserId = ref(null);
  
  const chatId = ref(route.query.chat_id || null);
  const ghostUserId = ref(route.query.ghost_user_id || null);
  const source = ref(route.query.source || route.query.dialogSource || "default");
  
  const interlocutor = computed(() => ({
    userId: route.query.user_id || ghostUserId.value,
    name: route.query.name || "Без имени",
    avatar: route.query.avatar || route.query.image || "/default-avatar.png",
    specifics: route.query.specifics || "Специальность не указана",
    is_online: false,
  }));
  
  const messages = ref([]);
  const newMessage = ref("");
  const messagesList = ref(null);
  const lastMessageId = ref(null);
  
  const isLoadingMessages = ref(true);
  const isGhostChat = ref(!chatId.value);
  const isDeleting = ref(false);
  
  const selectedMessage = ref(null);
  const showDeleteMenu = ref(false);

  const isInterlocutorTyping = ref(false);

  let typingTimer = null;
  let isTypingSent = false;
  let unsubscribeTyping = null;
  
  let unsubscribe = null;
  let pressTimer = null;
  
  const loadedImages = ref({});
  const downloadingFiles = ref({});
  const fileInput = ref(null);

  const observer = new IntersectionObserver(
    entries => {
      entries.forEach(entry => {
        const messageEl = entry.target;
        const messageId = messageEl.dataset.id;
  
        if (!entry.isIntersecting) return;
  
        const msg = messages.value.find(m => m.id === messageId);
  
        if (msg && !msg.isRead && msg.senderId !== currentUserId.value) {
          msg.isRead = true;
          ChatsService.markMessagesAsRead([msg.id]);
        }
      });
    },
    {
      threshold: 1.0,
    }
  );
  
  onMounted(async () => {
    const user = await authStateReady();
  
    if (!user) {
      router.replace("/");
      return;
    }
  
    currentUserId.value = user.uid;
  
    messages.value = [];
    lastMessageId.value = null;
    isLoadingMessages.value = true;
  
    await nextTick();
  
    if (chatId.value) {
      await loadMessages();
      startListening(chatId.value);
      listenTyping();
    } else {
      isGhostChat.value = true;
      isLoadingMessages.value = false;
    }
  
    scrollToBottom();
  });
  
  onUnmounted(() => {
    if (unsubscribe) unsubscribe();
    if (unsubscribeTyping) unsubscribeTyping();

    observer.disconnect();

    clearTimeout(pressTimer);
    clearTimeout(typingTimer);

    setTyping(false);
  });
  
  function goBack() {
    router.push("/m-dialogs");
  }
  
  function observeMessage(el, message) {
    if (!el || message.senderId === currentUserId.value || message.isRead) return;
  
    el.dataset.id = message.id;
    observer.observe(el);
  }
  
  function startListening(id) {
    if (!id) return;
  
    if (unsubscribe) {
      unsubscribe();
      unsubscribe = null;
    }
  
    unsubscribe = ChatsService.listenForNewMessages(id, newMessageData => {
      const messageData = {
        id: newMessageData.id,
        textMessage: newMessageData.textMessage,
        sendDate:
          typeof newMessageData.sendDate === "object" && newMessageData.sendDate.toDate
            ? newMessageData.sendDate.toDate().toISOString()
            : newMessageData.sendDate,
        senderId: newMessageData.senderId,
        chatId: newMessageData.chatId,
        isRead: newMessageData.isRead ?? false,
        is_deleted: newMessageData.is_deleted ?? false,
        isSending: false,
        isFailed: false,
        imageUrl: newMessageData.imageUrl || newMessageData.image_url || null,
        messageType: newMessageData.messageType || newMessageData.message_type || null,
        attachmentUrl: newMessageData.attachmentUrl || newMessageData.attachment_url || null,
        attachmentName: newMessageData.attachmentName || newMessageData.attachment_name || null,
        attachmentMimeType: newMessageData.attachmentMimeType || newMessageData.attachment_mime_type || null,
        attachmentSize: newMessageData.attachmentSize || newMessageData.attachment_size || null,
        storagePath: newMessageData.storagePath || newMessageData.storage_path || null,
      };
  
      const tempIndex = messages.value.findIndex(msg => {
        const isTemp = msg.id?.startsWith?.("temp-");
        const sameSender = msg.senderId === messageData.senderId;
        const sameText = msg.textMessage === messageData.textMessage;
        const notFailed = !msg.isFailed;
  
        return isTemp && sameSender && sameText && notFailed;
      });
  
      if (tempIndex !== -1) {
        messages.value[tempIndex] = {
          ...messages.value[tempIndex],
          ...messageData,
          isSending: false,
          isFailed: false,
        };
  
        messages.value = [...messages.value];
        nextTick(scrollToBottom);
        return;
      }
  
      const existingIndex = messages.value.findIndex(
        msg => msg.id === messageData.id
      );
  
      if (existingIndex !== -1) {
        messages.value[existingIndex] = {
          ...messages.value[existingIndex],
          isRead: messageData.isRead,
          is_deleted: messageData.is_deleted,
        };
  
        messages.value = [...messages.value];
        return;
      }
  
      messages.value.push(messageData);
      nextTick(scrollToBottom);
    });
  }
  
  async function loadMessages() {
    try {
      const response = await ChatsService.getChatMessages(
        chatId.value,
        20,
        lastMessageId.value,
        interlocutor.value?.userId
      );
  
      const fetchedMessages = response.messages || [];
  
      messages.value = fetchedMessages.map(msg => ({
        id: msg.message_id,
        textMessage: msg.text_message,
        sendDate: msg.send_date,
        senderId: msg.sender_id,
        chatId: msg.chat_id,
        isRead: msg.is_read ?? false,
        is_deleted: msg.is_deleted ?? false,
        isSending: false,
        isFailed: false,
        imageUrl: msg.image_url || null,
        messageType: msg.message_type || null,
        attachmentUrl: msg.attachment_url || null,
        attachmentName: msg.attachment_name || null,
        attachmentMimeType: msg.attachment_mime_type || null,
        attachmentSize: msg.attachment_size || null,
        storagePath: msg.storage_path || null,
      })).reverse();
  
      lastMessageId.value = response.nextLastMessageId || null;
      isGhostChat.value = response.is_new_chat ?? messages.value.length === 0;
  
      const unreadMessages = messages.value
        .filter(msg => msg.senderId !== currentUserId.value && !msg.isRead)
        .map(msg => msg.id);
  
      if (unreadMessages.length > 0) {
        await ChatsService.markMessagesAsRead(unreadMessages);
      }
    } catch (error) {
      console.error("❌ Ошибка загрузки сообщений:", error);
    } finally {
      isLoadingMessages.value = false;
    }
  }
  
  async function sendMessage() {
    const trimmedMessage = newMessage.value.trim();
  
    if (!trimmedMessage || !currentUserId.value) return;

    await setTyping(false);
    isTypingSent = false;
    clearTimeout(typingTimer);
  
    const localMessage = {
      id: `temp-${Date.now()}`,
      senderId: currentUserId.value,
      textMessage: trimmedMessage,
      sendDate: new Date().toISOString(),
      chatId: chatId.value,
      isRead: true,
      is_deleted: false,
      isSending: true,
      isFailed: false,
    };
  
    newMessage.value = "";
    messages.value.push(localMessage);
  
    nextTick(scrollToBottom);
  
    await sendMessageToBackend(localMessage);
  }
  
  async function sendMessageToBackend(localMessage) {
    try {
      const ghostMode = !chatId.value;
  
      const payload = {
        chat_id: chatId.value,
        sender_id: currentUserId.value,
        sender_name: "Пользователь",
        text_message: localMessage.textMessage,
        source: source.value ?? "default",
      };
  
      if (ghostMode) {
        payload.is_ghost_chat = true;
        payload.interlocutor_id = interlocutor.value.userId;
      }

      if (localMessage.attachmentUrl && localMessage.attachmentUrl !== "local-file") {
        payload.attachment_url = localMessage.attachmentUrl;
        payload.attachment_name = localMessage.attachmentName;
        payload.attachment_mime_type = localMessage.attachmentMimeType;
        payload.attachment_size = localMessage.attachmentSize;
        payload.storage_path = localMessage.storagePath;
      }
  
      const response = await ChatsService.sendMessage(payload);
  
      const success =
        response?.success === true ||
        response?.message_id ||
        response?.messageId;
  
      if (!success) {
        throw new Error(response?.error || "Message send failed");
      }
  
      localMessage.id =
        response.message_id ||
        response.messageId ||
        localMessage.id;
  
      localMessage.chatId =
        response.chat_id ||
        response.chatId ||
        chatId.value;
  
      localMessage.isSending = false;
      localMessage.isFailed = false;
  
      if (ghostMode && localMessage.chatId) {
        chatId.value = localMessage.chatId;
        isGhostChat.value = false;
  
        router.replace({
          path: "/m-chat",
          query: {
            chat_id: localMessage.chatId,
            user_id: interlocutor.value.userId,
            name: interlocutor.value.name,
            avatar: interlocutor.value.avatar,
            specifics: interlocutor.value.specifics,
            source: source.value,
          },
        });
  
        startListening(localMessage.chatId);
        listenTyping();
      }
  
      messages.value = [...messages.value];
      nextTick(scrollToBottom);
    } catch (error) {
      console.error("❌ sendMessage error:", error);
  
      localMessage.isSending = false;
      localMessage.isFailed = true;
  
      messages.value = [...messages.value];
    }
  }
  
  async function retryMessage(message) {
    if (!message?.isFailed || message.isSending) return;
  
    message.isFailed = false;
    message.isSending = true;
    message.sendDate = new Date().toISOString();
  
    messages.value = [...messages.value];
  
    nextTick(scrollToBottom);
  
    await sendMessageToBackend(message);
  }
  
  function scrollToBottom() {
    if (messagesList.value) {
      messagesList.value.scrollTop = messagesList.value.scrollHeight;
    }
  }
  
  function formatTime(dateString) {
    if (!dateString) return "";
  
    const cleanDateString = String(dateString).split(".")[0];
    const date = new Date(cleanDateString);
  
    if (isNaN(date)) return "";
  
    return date.toLocaleTimeString("ru-RU", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });
  }
  
  function startPressTimer(message) {
    pressTimer = setTimeout(() => {
      selectedMessage.value = message;
      showDeleteMenu.value = true;
    }, 600);
  }
  
  function cancelPressTimer() {
    clearTimeout(pressTimer);
  }
  
  async function deleteMessage() {
    if (isDeleting.value) return;
  
    isDeleting.value = true;
  
    try {
      const msgId = selectedMessage.value?.id;
      if (!msgId) return;
  
      let result;
  
      try {
        result = await ChatsService.deleteMessage(msgId, false);
      } catch (error) {
        console.warn("Мягкое удаление не удалось, пробуем force-удаление", error);
        result = await ChatsService.deleteMessage(msgId, true);
      }
  
      if (result.success) {
        const msg = messages.value.find(m => m.id === msgId);
  
        if (msg) {
          msg.is_deleted = true;
        }
  
        showDeleteMenu.value = false;
        selectedMessage.value = null;
      }
    } catch (error) {
      console.error("Ошибка при удалении сообщения:", error);
    } finally {
      isDeleting.value = false;
    }
  }


  async function setTyping(isTyping) {
  if (!chatId.value || !currentUserId.value) return;

  const db = getFirestore();

  const q = query(
    collection(db, "Chats"),
    where("chat_id", "==", chatId.value)
  );

  const snapshot = await getDocs(q);
  const batch = writeBatch(db);

  snapshot.docs.forEach(docSnap => {
    batch.update(docSnap.ref, {
      typing_user_id: isTyping ? currentUserId.value : null,
      typing_updated_at: serverTimestamp()
    });
  });

  await batch.commit();
}

  function handleTyping() {
    const hasText = newMessage.value.trim().length > 0;

    if (hasText && !isTypingSent) {
      setTyping(true);
      isTypingSent = true;
    }

    clearTimeout(typingTimer);

    typingTimer = setTimeout(() => {
      setTyping(false);
      isTypingSent = false;
    }, 1500);

    if (!hasText) {
      setTyping(false);
      isTypingSent = false;
    }
  }

  function listenTyping() {
    if (!chatId.value || !currentUserId.value || !interlocutor.value?.userId) return;

    if (unsubscribeTyping) {
      unsubscribeTyping();
      unsubscribeTyping = null;
    }

    const db = getFirestore();

    const q = query(
      collection(db, "Chats"),
      where("chat_id", "==", chatId.value),
      where("owner_id", "==", currentUserId.value)
    );

    unsubscribeTyping = onSnapshot(q, snapshot => {
      const data = snapshot.docs[0]?.data();
      if (!data) return;

      isInterlocutorTyping.value =
        data.typing_user_id === interlocutor.value.userId;
    });
  }

  function openFilePicker() {
    fileInput.value?.click();
  }

  </script>
  
  <style scoped>
  .m-chat-page {
    width: 100vw;
    height: 100vh;
    height: 100dvh;
    background: var(--background-color);
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }
  
  .m-chat-header {
    height: 62px;
    flex-shrink: 0;
    padding: calc(8px + env(safe-area-inset-top)) 12px 8px;
    background: var(--sidebar-background);
    border-bottom: 1px solid var(--separator_home);
    display: flex;
    align-items: center;
    box-sizing: content-box;
  }
  
  .back-button {
    width: 34px;
    height: 34px;
    border: none;
    background: transparent;
    color: var(--text-primary);
    font-size: 34px;
    line-height: 28px;
    cursor: pointer;
    margin-right: 6px;
  }
  
  .avatar {
    width: 38px;
    height: 38px;
    border-radius: 50%;
    object-fit: cover;
    margin-right: 10px;
    flex-shrink: 0;
  }
  
  .info {
    min-width: 0;
    display: flex;
    flex-direction: column;
  }
  
  .name {
    font-size: 15px;
    font-family: "Eastman-Bold", sans-serif;
    color: var(--text-primary);
    margin: 0;
    max-width: 220px;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
  
  .specifics {
    font-size: 11px;
    font-family: "Eastman-Regular", sans-serif;
    color: var(--text-secondary);
    margin: 2px 0 0;
    max-width: 220px;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
  
  .messages-list {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding: 12px 10px;
    background: var(--background-color);
    display: flex;
    flex-direction: column;
    gap: 10px;
    scrollbar-width: none;
    -webkit-overflow-scrolling: touch;
  }
  
  .message-item {
    display: flex;
    align-items: flex-start;
    padding: 9px 12px;
    border-radius: 18px;
    font-family: "Eastman-Regular", sans-serif;
    font-size: 14px;
    color: var(--text-primary);
    position: relative;
    word-wrap: break-word;
    max-width: 78%;
    margin-left: 42px;
    background: var(--chat-incoming-bg);
  }
  
  .own-message {
    background: #2173e5;
    color: white;
    align-self: flex-end;
    margin-left: auto;
    margin-right: 0;
  }
  
  .avatar-container {
    margin-right: 10px;
    margin-left: -48px;
  }
  
  .message-avatar {
    width: 32px;
    height: 32px;
    border-radius: 50%;
  }
  
  .message-content {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }
  
  .text {
    margin: 0;
    color: inherit;
    line-height: 1.35;
    word-break: break-word;
  }
  
  .time {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    font-size: 10px;
    color: var(--text-secondary);
    margin-top: 4px;
    gap: 4px;
  }
  
  .own-message .time {
    color: rgba(255, 255, 255, 0.75);
  }
  
  .read-icon {
    width: 13px;
    height: 13px;
    opacity: 0.8;
  }
  
  .message-input {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    padding: 10px 12px calc(10px + env(safe-area-inset-bottom));
    border-top: 1px solid var(--border-color);
    background: var(--sidebar-background);
    gap: 8px;
  }
  
  .message-field {
    flex: 1;
    min-width: 0;
    padding: 11px 14px;
    border-radius: 18px;
    outline: none;
    font-family: "Eastman-Regular", sans-serif;
    font-size: 14px;
    background-color: transparent;
    color: var(--text-primary);
    border: 1px solid var(--chat-input-border);
  }
  
  .message-field::placeholder {
    color: var(--text-secondary);
  }
  
  .file-button,
  .send-button {
    background: none;
    border: none;
    padding: 6px;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  .file-button img,
  .send-button img {
    width: 20px;
    height: 20px;
    opacity: var(--icon-opacity);
  }
  
  .send-button:disabled {
    opacity: 0.3;
    cursor: not-allowed;
  }
  
  .empty-chat-placeholder {
    flex: 1;
    min-height: 60vh;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    color: var(--text-secondary);
  }
  
  .empty-chat-icon {
    width: 88px;
    height: auto;
    margin-bottom: 12px;
  }
  
  .empty-chat-text {
    font-size: 16px;
    font-family: "Eastman-Medium", sans-serif;
    margin: 0;
    color: var(--text-primary);
  }
  
  .empty-chat-subtext {
    font-size: 13px;
    font-family: "Eastman-Regular", sans-serif;
    margin-top: 6px;
    color: var(--text-secondary);
  }
  
  .message-skeleton-wrapper {
    display: flex;
    align-items: flex-start;
    margin-bottom: 12px;
  }
  
  .message-skeleton-wrapper.own {
    justify-content: flex-end;
  }
  
  .message-skeleton-wrapper.interlocutor {
    justify-content: flex-start;
  }
  
  .skeleton-avatar {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    margin-right: 10px;
    flex-shrink: 0;
    background-color: #ebebeb;
  }
  
  .message-skeleton-bubble {
    padding: 12px 16px;
    border-radius: 16px;
    max-width: 70%;
    background-color: #ebebeb;
  }
  
  .skeleton-line {
    height: 10px;
    border-radius: 6px;
    margin-bottom: 6px;
    background-color: #ebebeb;
  }
  
  .skeleton-line.short {
    width: 50%;
  }
  
  .shimmer {
    background: linear-gradient(
      to right,
      #e4e4e4 0%,
      #e8e8e8 20%,
      #e5e5e5 40%
    );
    background-size: 1000px 100%;
    animation: shimmer 1.5s infinite linear;
  }
  
  @keyframes shimmer {
    0% {
      background-position: -1000px 0;
    }
    100% {
      background-position: 1000px 0;
    }
  }
  
  .delete-popup-overlay {
    position: fixed;
    inset: 0;
    background-color: rgba(0, 0, 0, 0.42);
    display: flex;
    align-items: flex-end;
    justify-content: center;
    z-index: 999;
    padding: 16px;
    box-sizing: border-box;
  }
  
  .delete-popup {
    width: 100%;
    max-width: 360px;
    background: var(--sidebar-background);
    border-radius: 18px;
    padding: 18px;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.2);
    text-align: center;
    margin-bottom: env(safe-area-inset-bottom);
  }
  
  .delete-popup p {
    font-size: 14px;
    margin-bottom: 16px;
    color: var(--text-primary);
    font-family: "Eastman-Regular", sans-serif;
  }
  
  .delete-button,
  .cancel-button {
    width: 100%;
    padding: 12px 14px;
    font-size: 14px;
    border-radius: 12px;
    font-family: "Eastman-Medium", sans-serif;
    border: none;
    cursor: pointer;
    margin-top: 8px;
  }
  
  .delete-button {
    background-color: #ff3b30;
    color: white;
  }
  
  .cancel-button {
    background-color: var(--input-background);
    color: var(--text-primary);
  }
  
  .text.deleted {
    font-family: sans-serif;
    font-style: italic;
    font-weight: 300;
    font-size: 14px;
    color: var(--text-secondary);
    opacity: 0.65;
  }
  
  @media (min-width: 768px) {
    .m-chat-page {
      max-width: 430px;
      margin: 0 auto;
      border-left: 1px solid var(--separator_home);
      border-right: 1px solid var(--separator_home);
    }
  }

  .message-item {
  width: 100%;
  max-width: none;
  margin-left: 0;
  margin-right: 0;
  padding: 0;
  background: transparent;
  border-radius: 0;

  display: flex;
  align-items: flex-end;
}

.message-item:not(.own-message) {
  align-self: stretch;
  justify-content: flex-start;
  background: transparent;
}

.own-message {
  align-self: stretch;
  justify-content: flex-end;
  background: transparent;
  margin-left: 0;
  margin-right: 0;
}

.message-content {
  width: fit-content;
  max-width: 72%;
  padding: 9px 12px;
  border-radius: 18px;
  background: var(--chat-incoming-bg);
}

.own-message .message-content {
  background: #2173e5;
  color: white;
}

.avatar-container {
  margin-left: 0;
  margin-right: 8px;
  flex-shrink: 0;
}

.message-avatar {
  width: 32px;
  height: 32px;
  margin: 0;
  flex-shrink: 0;
}

.specifics.typing {
  color: #2173e5;
  font-family: "Eastman-Medium", sans-serif;
}

.message-image-wrapper {
  position: relative;
  width: min(260px, 68vw);
  aspect-ratio: 4 / 3;
  border-radius: 14px;
  overflow: hidden;
  background: #e8e8e8;
}

.message-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  display: block;
  opacity: 0;
  transition: opacity 0.25s ease;
}

.message-image.loaded {
  opacity: 1;
}

.file-message {
  display: flex;
  align-items: center;
  gap: 10px;
  width: min(260px, 68vw);
}

.attach-spinner {
  width: 18px;
  height: 18px;
  border: 2px solid rgba(33, 115, 229, 0.25);
  border-top-color: #2173e5;
  border-radius: 50%;
  animation: attachSpin 0.7s linear infinite;
}

@keyframes attachSpin {
  to { transform: rotate(360deg); }
}

  </style>